import { ReplaceSaiSurveyProspectsErrorCode } from '../../../../graphQL';

export const ACCEPTED_TYPES = ['.csv'];

type ProspectErrorCopy = {
  /** Complete, self-contained message: what went wrong and what to do about it. */
  message: string;
  /**
   * Whether to show the API's own message underneath. True only for codes whose
   * message pinpoints the problem in the file — the offending rows, exact counts,
   * or a parse position — detail we cannot reproduce here and the admin needs in
   * order to fix the file. Never true for a code whose message names internals.
   */
  showApiDetail?: boolean;
};

export const PROSPECT_ERROR_COPY: Record<ReplaceSaiSurveyProspectsErrorCode, ProspectErrorCopy> = {
  // --- Something broke on our side. The API messages name internals
  // (e.g. "upload 41 is not for survey 12"), so they stay hidden.
  [ReplaceSaiSurveyProspectsErrorCode.SurveyNotFound]: {
    message: 'Something went wrong preparing this upload. Please try again.',
  },
  [ReplaceSaiSurveyProspectsErrorCode.UploadNotFound]: {
    message: 'Something went wrong preparing this upload. Please try again.',
  },
  [ReplaceSaiSurveyProspectsErrorCode.UploadNotForSurvey]: {
    message: 'Something went wrong preparing this upload. Please try again.',
  },
  [ReplaceSaiSurveyProspectsErrorCode.InvalidUploadContentType]: {
    message: 'Something went wrong preparing this upload. Please try again.',
  },

  // --- The file is unusable as a whole.
  [ReplaceSaiSurveyProspectsErrorCode.InvalidCsvFormat]: {
    message:
      'The uploaded file could not be read as a CSV. Re-export it as comma-separated values from your ' +
      'spreadsheet tool and upload it again.',
    /**
     * The parser's own message is the only thing that says *where* the file went
     * wrong, and the ingest passes through the first CsvParseError specifically
     * because those carry positions and never file content — so this is safe to
     * show and useless to withhold. Do not "tidy" this to false.
     */
    showApiDetail: true,
  },
  [ReplaceSaiSurveyProspectsErrorCode.MissingEmailColumn]: {
    message:
      'The uploaded CSV needs a column named "email" (spelled in all lowercase — "Email" and "E-mail" are ' +
      'not recognized). Rename or add the column, then upload the file again. The "ssoUniqueId" and ' +
      '"cohort" columns are optional.',
  },
  [ReplaceSaiSurveyProspectsErrorCode.EmptyCsv]: {
    message:
      'The uploaded CSV has no student rows in it. Add at least one row below the header and upload it again.',
  },
  [ReplaceSaiSurveyProspectsErrorCode.UploadAlreadyProcessed]: {
    message:
      'The uploaded file has already been processed. Upload it again if you need to make ' +
      'another change.',
  },
  [ReplaceSaiSurveyProspectsErrorCode.MalwareDetected]: {
    message:
      'The uploaded file did not pass our security scan, so nothing was saved. Try re-exporting it from ' +
      'your spreadsheet tool, or submit a product issue if this keeps happening.',
  },

  // --- Specific rows are wrong. The API message lists them.
  // BlankRow rejoins this group when PDE-1418 adds the remaining row rules.
  [ReplaceSaiSurveyProspectsErrorCode.InvalidEmail]: {
    message:
      'Some email addresses are invalid or empty. Correct them in your CSV and upload it again.',
    showApiDetail: true,
  },
  [ReplaceSaiSurveyProspectsErrorCode.InvalidEmailDomain]: {
    message:
      'Some emails in the uploaded file have ineligible email domains for this organization. Remove or update ' +
      "these student emails, or add the additional domain to the organization's settings, then upload the file again.",
    showApiDetail: true,
  },
  [ReplaceSaiSurveyProspectsErrorCode.DuplicateEmail]: {
    message:
      'The same email appears more than once. Remove the duplicate row(s) and upload the file again.',
    showApiDetail: true,
  },
  [ReplaceSaiSurveyProspectsErrorCode.InvalidSsoUniqueId]: {
    message:
      'The uploaded file contains at least one SSO unique ID that is too long. Shorten to 255 characters or fewer and upload the ' +
      'file again.',
    showApiDetail: true,
  },
  [ReplaceSaiSurveyProspectsErrorCode.DuplicateSsoUniqueId]: {
    message:
      'The same SSO unique ID is used for more than one student. Each student needs their own, so ' +
      'correct the duplicates and upload the file again.',
    showApiDetail: true,
  },
  [ReplaceSaiSurveyProspectsErrorCode.InvalidCohort]: {
    message:
      'The uploaded file contains at least one cohort name that is too long. Shorten to 255 characters or fewer ' +
      'and upload the file again.',
    showApiDetail: true,
  },
};

/** Shown when the upload fails before any error code comes back (network, S3). */
export const GENERIC_UPLOAD_ERROR_MESSAGE =
  'There was a problem uploading this file. Please try again.';

export const MULTIPLE_FILES_MESSAGE =
  'Only one file can be uploaded at a time. Drop a single CSV containing the full list — ' +
  'it replaces the whole roster, so it does not need to be split.';

export const NOT_A_CSV_MESSAGE =
  'The selected file is not a CSV. Export your original file as a .csv file and upload it again.';
